import {
  CartDrawerCard,
  getCartItemWeight,
  hideStrikethroughPrice,
  productPhotoMetadata,
} from '@iheartjane/menu';
import type { AppMode, ReservationMode } from '@iheartjane/types';
import { formatCurrency } from '@iheartjane/utils';

import {
  useOptimizeSpecials,
  useRemoveCartProduct,
  useUpdateCartProduct,
} from '@jane/shared-ecomm/data-access';
import { useMenu } from '@jane/shared-ecomm/providers';
import {
  useNeedsSignInForEligibility,
  useShouldShowGold,
} from '@jane/shared/hooks';
// eslint-disable-next-line no-restricted-imports
import type { PendingCartProduct } from '@jane/shared/models';
import {
  calculateDiscountPercentage,
  getOriginalAndDiscountedTotalPrices,
} from '@jane/shared/util';

interface QuickCartDrawerCardProps {
  appMode: AppMode;
  cartUuid: string;
  product: PendingCartProduct;
  reservationMode: ReservationMode;
}

export const QuickCartDrawerCard = ({
  appMode,
  cartUuid,
  product,
  reservationMode,
}: QuickCartDrawerCardProps) => {
  const { store } = useMenu();
  const shouldShowGold = useShouldShowGold({
    ...(store.id ? { store: { id: store.id, state: store.state } } : {}),
  });
  const needsSignInForEligibility = useNeedsSignInForEligibility({
    cartProduct: product,
  });

  const { mutate: optimizeSpecials } = useOptimizeSpecials();
  const handleCartProductChange = () => {
    if (cartUuid) {
      optimizeSpecials({
        app_mode: appMode,
        reservation_mode: reservationMode,
        uuid: cartUuid,
        //currently users cannot enter promo codes or group special ids from quick chekcout
        promo_code: undefined,
        user_group_special_id: undefined,
      });
    }
  };
  const { mutate: updateProduct } = useUpdateCartProduct({
    onSuccess: handleCartProductChange,
  });
  const { mutate: removeProduct } = useRemoveCartProduct({
    onSuccess: handleCartProductChange,
  });

  const {
    amount,
    name,
    brand,
    brand_subtype,
    category,
    brand_discount_label,
    brand_discount_title,
    count,
    price_id,
    special_title,
    max_cart_quantity,
  } = product;
  const brandDiscountText = brand_discount_title || brand_discount_label;
  const showWeight = price_id !== 'each' && !amount;
  const weightString = showWeight ? `/${getCartItemWeight(product)}` : '';
  const { url } = productPhotoMetadata(product);

  const { originalPrice, discountedPrice: unformattedDiscountedPrice } =
    getOriginalAndDiscountedTotalPrices({
      menuProduct: product,
      count,
      priceId: price_id,
    });
  //if unformattedDiscountedPrice is undefined, there is no discount, so we use originalPrice
  const discountedPrice = unformattedDiscountedPrice ?? originalPrice;

  const isDiscounted = Boolean(
    discountedPrice && originalPrice !== discountedPrice
  );
  const fullPriceIsDiscounted =
    formatCurrency((originalPrice ?? 0) * count) !==
    formatCurrency((discountedPrice ?? 0) * count);
  const discountPercentage =
    calculateDiscountPercentage(originalPrice, discountedPrice) ?? 0;

  return (
    <CartDrawerCard
      brand={brand}
      brandDiscountText={brandDiscountText}
      brandSubtype={brand_subtype}
      category={category}
      count={count}
      discountPercentage={discountPercentage}
      fullPriceIsDiscounted={fullPriceIsDiscounted}
      hideStrikethrough={hideStrikethroughPrice(store?.state || '')}
      isDiscounted={isDiscounted}
      maxCartQuantity={max_cart_quantity}
      name={name}
      needsSignInForEligibility={needsSignInForEligibility}
      onEditQuantity={(qty: number) => {
        updateProduct({
          cart_uuid: cartUuid,
          product: {
            price_id: product.price_id,
            product_id: product.product_id,
            count: qty,
          },
        });
      }}
      onRemoveProduct={() => {
        removeProduct({
          cart_uuid: cartUuid,
          product_id: product.product_id,
        });
      }}
      photoUrl={url}
      productFullPrice={originalPrice ?? 0}
      productPrice={discountedPrice ?? null}
      shouldShowGold={shouldShowGold && !!brandDiscountText}
      specialTitle={special_title}
      weightString={weightString}
    />
  );
};
